const TRANSITION_MS = 250

export default function InitBrandSlider() {
    // pagination is a purely visual frontend behavior — it must never run
    // in the backend, where editors need every logo visible/selectable at
    // once (see the ".neos-backend" override in BrandSlider.scss). Without
    // this bail-out, the inline "display: none" this script sets on
    // out-of-page items would win over that CSS override (inline style
    // always beats a stylesheet rule) and hide logos beyond the first page
    // from the editor entirely.
    if (window.neos || document.body.classList.contains('neos-backend')) {
        return
    }

    document.querySelectorAll('[data-brand-slider]').forEach((galleryEl) => {
        const listEl = galleryEl.querySelector('[data-brand-slider-list]')
        const items = Array.from(listEl ? listEl.querySelectorAll('[data-brand-slider-item]') : [])

        if (!items.length) {
            return
        }

        const collectionEl = listEl.querySelector('.neos-contentcollection')
        const prevButton = galleryEl.querySelector('[data-brand-slider-prev]')
        const nextButton = galleryEl.querySelector('[data-brand-slider-next]')
        const desktopPerRow = parseInt(galleryEl.dataset.brandSliderPerRow, 10) || 4

        // desktop uses the editor-chosen column count, smaller screens cap
        // it down — matches the same breakpoints as ImageSlider's "double"
        // layout so this behaves consistently with the rest of the site
        function getColumns() {
            if (window.matchMedia('(max-width: 768px)').matches) {
                return 2
            }
            if (window.matchMedia('(max-width: 991px)').matches) {
                return Math.min(desktopPerRow, 3)
            }
            return desktopPerRow
        }

        let columns = getColumns()
        let pageSize = columns * 2
        let page = 0
        let isAnimating = false

        function render() {
            if (collectionEl) {
                collectionEl.style.setProperty('--brand-slider-columns', columns)
            }

            const pageCount = Math.ceil(items.length / pageSize)
            page = Math.min(page, pageCount - 1)

            // a page's start index is clamped so it never runs past
            // "items.length - pageSize" — the last page then slides back
            // and shows a full grid (re-showing a few items also visible on
            // the previous page) instead of a mostly-empty grid with just a
            // handful of logos in the top-left corner
            const start = Math.min(page * pageSize, Math.max(0, items.length - pageSize))
            const end = start + pageSize

            items.forEach((item, index) => {
                item.style.display = index >= start && index < end ? '' : 'none'
            })

            const hasMultiplePages = pageCount > 1
            if (prevButton) {
                prevButton.style.display = hasMultiplePages ? '' : 'none'
            }
            if (nextButton) {
                nextButton.style.display = hasMultiplePages ? '' : 'none'
            }
        }

        // slides the whole page out in the direction of travel, swaps which
        // items are visible while it's faded out, then slides the new page
        // in from the opposite side — a plain opacity/transform transition
        // on the grid container itself, not a real continuous multi-page
        // track (that would need restructuring the real ContentCollection
        // nodes into per-page wrapper groups, which risks the exact "editor
        // can no longer manage the real nodes" bug hit building ImageGallery)
        function goToPage(direction) {
            const pageCount = Math.ceil(items.length / pageSize)
            if (pageCount <= 1 || isAnimating) {
                return
            }

            if (!collectionEl) {
                page = (page + direction + pageCount) % pageCount
                render()
                return
            }

            isAnimating = true
            collectionEl.style.setProperty('--brand-slider-direction', direction > 0 ? '1' : '-1')
            collectionEl.classList.add('is-leaving')

            window.setTimeout(() => {
                page = (page + direction + pageCount) % pageCount
                render()

                collectionEl.classList.remove('is-leaving')
                collectionEl.classList.add('is-entering')

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        collectionEl.classList.remove('is-entering')
                        window.setTimeout(() => {
                            isAnimating = false
                        }, TRANSITION_MS)
                    })
                })
            }, TRANSITION_MS)
        }

        if (prevButton) {
            prevButton.addEventListener('click', () => goToPage(-1))
        }
        if (nextButton) {
            nextButton.addEventListener('click', () => goToPage(1))
        }

        window.addEventListener('resize', () => {
            const nextColumns = getColumns()
            if (nextColumns !== columns) {
                columns = nextColumns
                pageSize = columns * 2
                page = 0
                render()
            }
        })

        render()
    })
}
